import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import cookies from 'js-cookie';
import axios from 'axios';

import { api } from '../../api';
import { sentryLogError } from '../../utils';
import type { IDraftCookie } from '../../models/form-data';

import { setProducts, setServices } from './basket';
import type { IDraftSlice, TCreateThunkApi, IInitDraft } from './models';
import { setStep, setStatus, setUserStatus, setRequestCodeWord, setAppIds } from './application';
import { setUserBirthDate, setCardId } from './first-step';

const initialState: IDraftSlice = {
    draftId: '',
    cookieParams: {
        cookieName: 'dc-form-draft',
        expires: 30,
    },
    draftEnabled: false,
    cookieData: undefined,
};

export const draftSlice = createSlice({
    name: 'draft',
    initialState,
    reducers: {
        setDraftId: (state, action: PayloadAction<IDraftSlice['draftId']>) => {
            state.draftId = action.payload;
        },
        setCookieParams: (state, action: PayloadAction<IDraftSlice['cookieParams']>) => {
            state.cookieParams = action.payload;
        },
        setDraftEnabled: (state, action: PayloadAction<IDraftSlice['draftEnabled']>) => {
            state.draftEnabled = action.payload;
        },
        setCookieData: (state, action: PayloadAction<IDraftSlice['cookieData']>) => {
            state.cookieData = action.payload;
        },
        setShortMobilePhone: (state, action: PayloadAction<IDraftSlice['shortMobilePhone']>) => {
            state.shortMobilePhone = action.payload;
        },
    },
});

export const { setCookieData, setCookieParams, setDraftEnabled, setDraftId, setShortMobilePhone } =
    draftSlice.actions;

/** Установка куки черновка */
export const setDraftCookie = createAsyncThunk<void, string, TCreateThunkApi>(
    'setDraftCookie',
    (draftId, { dispatch, getState }) => {
        const {
            draft: { cookieParams },
            firstStep: { cardId, contractId, firstName, middleName, packageId },
        } = getState();

        if (cookieParams?.cookieName) {
            dispatch(setDraftId(draftId));

            if (!firstName || !middleName || !cardId || !packageId || !contractId) {
                sentryLogError({
                    message: 'applicationsDraftRestore error',
                    data: {
                        draftId,
                        firstName,
                        middleName,
                        cardId,
                        packageId,
                        contractId,
                    },
                });

                return;
            }

            const cookieData: IDraftCookie = {
                firstName,
                middleName,
                cardId,
                draftId,
                packetId: packageId,
                prefillContractId: contractId,
            };

            cookies.set(cookieParams?.cookieName, JSON.stringify(cookieData), {
                expires: cookieParams.expires,
            });
        }
    }
);

/** Получание данных из куки черновика */
export const getDraftCookie = createAsyncThunk<void, undefined, TCreateThunkApi>(
    'getDraftCookie',
    (_, { dispatch, getState }) => {
        const {
            draft: { cookieParams },
        } = getState();

        if (!cookieParams?.cookieName) return;

        const cookie = cookies.get(cookieParams?.cookieName);

        if (!cookie) return;

        try {
            const cookieData = JSON.parse(cookie);

            dispatch(setCookieData(cookieData));
        } catch (error) {
            console.error(error);

            sentryLogError({
                error,
                message: 'Ошибка обработки куки черновика',
                data: {
                    cookie: cookie,
                },
            });
        }
    }
);

/** Удаление куки черновка */
export const deleteDraftCookie = createAsyncThunk<void, undefined, TCreateThunkApi>(
    'deleteDraftCookie',
    (_, { getState }) => {
        const {
            draft: { cookieParams },
        } = getState();

        if (cookieParams?.cookieName) {
            cookies.remove(cookieParams?.cookieName);
        }
    }
);

/** Получение данных по черновику */
export const restoreDraft = createAsyncThunk<void, string, TCreateThunkApi>(
    'restoreDraft',
    (id, { dispatch }) => {
        api.draftRestore(id)
            .then(response => {
                const { data, headers } = response;
                const {
                    additionalProducts,
                    additionalServices,
                    appId,
                    cardId,
                    passportBirthDate,
                    requestCodeWord,
                    shortMobilePhone,
                    userStatus,
                } = data;

                if (additionalProducts) {
                    dispatch(setProducts(additionalProducts));
                }

                if (additionalServices) {
                    dispatch(setServices(additionalServices));
                }
                dispatch(setUserStatus(userStatus));
                dispatch(setUserBirthDate(passportBirthDate));
                dispatch(setCardId(cardId));
                dispatch(setRequestCodeWord(requestCodeWord));
                dispatch(setAppIds({ appId, businessUid: headers.businessuid }));
                dispatch(setShortMobilePhone(shortMobilePhone));
                dispatch(setStep('delivery'));
            })
            .catch(error => {
                sentryLogError({
                    error,
                    message: 'applicationsDraftRestore error',
                    data: {
                        draftId: id,
                    },
                });

                dispatch(setStep('error'));

                if (!axios.isAxiosError(error)) return;

                dispatch(setStatus(error?.response?.data?.status));
            });
    }
);

/** Управление черновиком при инициализации */
export const handleDraft = createAsyncThunk<
    void,
    { cardId: string; draftId?: string },
    TCreateThunkApi
>('handleDraft', ({ cardId, draftId }, { dispatch, getState }) => {
    if (draftId) {
        dispatch(restoreDraft(draftId));

        return;
    }

    dispatch(getDraftCookie());

    const {
        draft: { cookieData, draftEnabled },
    } = getState();

    if (cookieData && draftEnabled) {
        const { cardId: id } = cookieData;

        if (cardId === id) {
            dispatch(setStep('draft'));
        }
    }
});

/** Отказ восстанавливать черновик */
export const declineRestoreDraft = createAsyncThunk<void, undefined, TCreateThunkApi>(
    'declineRestoreDraft',
    (_, { dispatch }) => {
        dispatch(deleteDraftCookie());
        dispatch(setStep('firstStep'));
    }
);

/** Инициализация черновиков */
export const initDraft = createAsyncThunk<void, IInitDraft, TCreateThunkApi>(
    'initDraft',
    ({ cardId, cookieParams, draftId, isDraftEnabled }, { dispatch }) => {
        /** Устанавливает параметры для куки черновика */
        if (cookieParams) {
            dispatch(setCookieParams(cookieParams));
        }

        if (isDraftEnabled) {
            dispatch(setDraftEnabled(true));
        }

        /** Устанавливает id черновика */

        if (draftId) {
            dispatch(setDraftId(draftId));
        }

        /** Перекидывает на экран черновика */

        dispatch(handleDraft({ draftId, cardId }));
    }
);
